import { siteApiSlice } from "../../app/siteApiSlice";
import { Accessibility, UserRole } from "../../app/userSlice";
import NamedEntity from "../../interfaces/NamedEntity";
import { axStringToAccessibility } from "../accessibility/accessibilityUtil";
import { RulesModel } from "../account/accountApiSlice";

export const Gender = {
	Female: "Female",
	Male: "Male",
};

export const Ethnicity = {
	AfricanAmerican: "African-American",
	AsianPacificIslander: "Asian and Pacific Islander",
	Hispanic: "Hispanic",
	NativeAmerican: "Native American",
	White: "White",
};

interface UserSelfBaseModel {
	userId: number;
	role: UserRole;
	isTeachersWriterAccount: boolean;
	username: string;
	firstName: string;
	lastName: string;
	initials: string;
	userImageUploadDisabled: boolean;
	hasImage: boolean;
	grade: string;
	requiresAccountConfirmation: boolean;
	bypassTermsOfService: boolean;
	school: NamedEntity;
	district: NamedEntity;
	cleverId: string;
}

export interface ProctorUserModel {
	rules: RulesModel;
	id: number;
	userName: string;
	fName: string;
	lName: string;
	initials: string;
	dateOfBirth: string;
	externalID: string;
	gradeId: number;
	scoringLevelId: number;
	password: string;
	isCleverUser: boolean;
	isClassLinkUser: boolean;
	allowUserImages: boolean;
	userImageDisabledBy: string;
	hasImage: boolean;
	isEditing: boolean;
	ethnicity?: string;
	gender?: string;
	economicallyDisadvantaged?: boolean;
	limitedEnglish?: boolean;
}

export interface CourseWritersResetPasswordViewModel {
	userId: number;
	password: string;
	confirmPassword: string;
}

export interface UserSelfViewModel extends UserSelfBaseModel {
	accessibility: string | null;
}

export interface UserSelfModel extends UserSelfBaseModel {
	accessibility: Accessibility | null;
}

export interface SaveAccessibilityModel {
	accessibility: string;
}

export const usersApiSlice = siteApiSlice.injectEndpoints({
	endpoints: (build) => ({
		getSelf: build.mutation<UserSelfModel, void>({
			query: () => ({
				url: `/User/GetSelf`,
				method: "GET",
			}),
			transformResponse: (response: UserSelfViewModel, meta, arg): UserSelfModel => {
				const transform: UserSelfModel = { ...response, accessibility: null };
				if (response.accessibility) {
					transform.accessibility = axStringToAccessibility(response.accessibility);
				}
				return transform;
			},
		}),
		saveAccessibility: build.mutation<string, string | null>({
			query: (payload) => ({
				url: `/User/SaveAccessibility`,
				method: "POST",
				body: { accessibility: payload },
				formData: true,
			}),
		}),
		editStudentInfo: build.query<ProctorUserModel, number>({
			query: (id) => ({
				url: `/CourseWriters/EditStudent`,
				params: { id },
			}),
			providesTags: ["ManageCourse", "CurrentCourse"],
		}),
		editStudent: build.mutation<void, ProctorUserModel>({
			query: (payload) => {
				return {
					url: `/CourseWriters/EditStudent`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["ManageCourse"],
		}),
		resetStudentPassword: build.mutation<void, CourseWritersResetPasswordViewModel>({
			query: (payload) => {
				return {
					url: `/CourseWriters/ResetPassword`,
					method: "POST",
					body: payload,
				};
			},
			invalidatesTags: ["ManageCourse"],
		}),
	}),
});

export const {
	useGetSelfMutation,
	useSaveAccessibilityMutation,
	useEditStudentInfoQuery,
	useEditStudentMutation,
	useResetStudentPasswordMutation,
} = usersApiSlice;
